import React, { useRef, useEffect } from "react";

const AudioWave = ({ stream, isRecording }) => {
  const canvasRef = useRef(null);
  const animationIdRef = useRef(null);
  const audioContextRef = useRef(null);

  // Ensure proper cleanup when component unmounts or dependencies change
  useEffect(() => {
    console.log(`AudioWave: isRecording=${isRecording}, stream=${stream ? 'present' : 'absent'}`);
    
    if (!isRecording || !stream) {
      if (animationIdRef.current) {
        console.log("AudioWave: canceling animation frame on dependency change");
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      clearCanvas();
      return;
    }

    try {
      console.log("AudioWave: initializing AudioContext");
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log(`AudioWave: AudioContext created with sample rate ${audioContext.sampleRate}Hz`);
      audioContextRef.current = audioContext;
      
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      console.log(`AudioWave: Analyser created with fftSize=${analyser.fftSize}`);

      analyser.fftSize = 2048; // Determines the frequency resolution
      const bufferLength = analyser.frequencyBinCount;
      console.log(`AudioWave: Buffer length set to ${bufferLength}`);
      const dataArray = new Uint8Array(bufferLength);

      source.connect(analyser);
      console.log("AudioWave: Source connected to analyser");

      const canvas = canvasRef.current;
      if (!canvas) {
        console.error("AudioWave: Canvas reference is null");
        return;
      }
      
      const canvasCtx = canvas.getContext("2d");
      console.log(`AudioWave: Canvas context obtained, dimensions: ${canvas.width}x${canvas.height}`);

      const draw = () => {
        if (!canvasRef.current) {
          console.warn("AudioWave: Canvas reference lost during animation");
          cancelAnimationFrame(animationIdRef.current);
          return;
        }
        
        analyser.getByteTimeDomainData(dataArray);

        canvasCtx.fillStyle = "white"; // Background color
        canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

        canvasCtx.lineWidth = 2;
        canvasCtx.strokeStyle = "#00B3B3"; // Teal color for the wave

        canvasCtx.beginPath();

        const sliceWidth = (canvas.width * 1.0) / bufferLength;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const v = dataArray[i] / 128.0; // Normalize between 0 and 2
          const y = (v * canvas.height) / 2;

          if (i === 0) {
            canvasCtx.moveTo(x, y);
          } else {
            canvasCtx.lineTo(x, y);
          }

          x += sliceWidth;
        }

        canvasCtx.lineTo(canvas.width, canvas.height / 2);
        canvasCtx.stroke();

        animationIdRef.current = requestAnimationFrame(draw);
      };

      console.log("AudioWave: Starting animation loop");
      draw();
    } catch (err) {
      console.error("Error initializing audio visualizer:", err);
    }

    return () => {
      console.log("AudioWave: Running cleanup function");
      if (animationIdRef.current) {
        console.log("AudioWave: Canceling animation frame on cleanup");
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      
      if (audioContextRef.current) {
        try {
          console.log("AudioWave: Closing AudioContext");
          audioContextRef.current.close();
        } catch (err) {
          console.error("Error closing audio context:", err);
        }
      }
      
      clearCanvas();
      console.log("AudioWave: Cleanup complete");
    };
  }, [isRecording, stream]);

  const clearCanvas = () => {
    if (!canvasRef.current) {
      console.log("AudioWave: No canvas to clear");
      return;
    }
    
    try {
      console.log("AudioWave: Clearing canvas");
      const canvas = canvasRef.current;
      const canvasCtx = canvas.getContext("2d");
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
    } catch (err) {
      console.error("Error clearing canvas:", err);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={50}
      style={{
        display: isRecording ? "block" : "none",
        margin: "auto",
        borderRadius: "4px",
      }}
    />
  );
};

export default AudioWave;
